import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  getOrderListAsync,
  getIdVerificationAsync,
  getIdVerificationAwaitCountAsync,
  getPreApprovedProductCustomerListAsync,
} from "./reduxSlice/actionsAsync";
import {
  selectIdChecks,
  selectOrderList,
  selectOrderListLoader,
  selectTotalOrders,
  selectTotalIdChecks,
  selectPreApprovedProductCustomerList,
  selectTotalPreApprovedProductCustomer,
} from "./reduxSlice/selector";
import "./index.scss";
import { ClinicalReviewPageProps } from "./ClinicalReviewPage";
import { fetchOrderListTypes } from "./ClinicalReviewApi";
import {
  ClinicalReviewRouteCategory,
  ClinicalReviewStatus,
  CLINICAL_REVIEW_ID_CHECK_CATEGORY,
  CLINICAL_REVIEW_PREAPPROVED_PRODUCT_CATEGORY,
  CLINICAL_REVIEW_STATUSES,
  isClinicalReviewSpecialCategory,
  usesAscendingSortByDefault,
} from "./constants";

const ORDER_LIMIT = 10;

export default (WrappedComponent: React.FC<ClinicalReviewPageProps>) => () => {
  const dispatch = useAppDispatch();
  const orderList = useAppSelector(selectOrderList);
  const totalOrders = useAppSelector(selectTotalOrders);
  const totalIdChecks = useAppSelector(selectTotalIdChecks);
  const orderListLoader = useAppSelector(selectOrderListLoader);
  const idChecks = useAppSelector(selectIdChecks);
  const preApprovedProductCustomerList = useAppSelector(
    selectPreApprovedProductCustomerList,
  );
  const totalPreApprovedProductCustomer = useAppSelector(
    selectTotalPreApprovedProductCustomer,
  );
  const [orderListParams, setOrderListParams] = useState<
    {
      currentPage: number;
      category?: ClinicalReviewRouteCategory;
      status: ClinicalReviewStatus;
    } & Pick<
      fetchOrderListTypes,
      "sortKey" | "isAscending" | "hideSubscriptions"
    >
  >({
    currentPage: 0,
    status: CLINICAL_REVIEW_STATUSES.AWAITING_REVIEW,
    sortKey: "date",
    isAscending: false,
    hideSubscriptions: false,
  });

  const { category, status: orderStatus } = useParams<{
    category?: ClinicalReviewRouteCategory;
    status: ClinicalReviewStatus;
  }>();

  useEffect(() => {
    /**
     * Reset the page number to 1 if status or category change (filter selected)
     */
    setOrderListParams((params) => ({
      ...params,
      currentPage: 1,
      status: orderStatus ?? CLINICAL_REVIEW_STATUSES.AWAITING_REVIEW,
      category,
      sortKey:
        category === CLINICAL_REVIEW_PREAPPROVED_PRODUCT_CATEGORY
          ? "nextReviewDate"
          : "date",
      isAscending: false,
      hideSubscriptions: false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, orderStatus]); //categoryFilter

  useEffect(() => {
    // trigger only if currentPage is not 0, to avoid call on initialize
    !!orderListParams.currentPage &&
      !isClinicalReviewSpecialCategory(orderListParams.category) &&
      dispatch(
        getOrderListAsync({
          orderId: orderListParams.currentPage,
          limit: ORDER_LIMIT,
          category: orderListParams.category,
          orderStatus: orderListParams.status,
          sortKey: orderListParams.sortKey,
          isAscending: orderListParams.isAscending,
          hideSubscriptions: orderListParams.hideSubscriptions,
        }),
      );
    !!orderListParams.currentPage &&
      orderListParams.category ===
        CLINICAL_REVIEW_PREAPPROVED_PRODUCT_CATEGORY &&
      dispatch(
        getPreApprovedProductCustomerListAsync({
          limit: ORDER_LIMIT,
          page: orderListParams.currentPage.toString(),
          sortKey: orderListParams.sortKey,
          isAscending: orderListParams.isAscending,
        }),
      );
    !!orderListParams.currentPage &&
      dispatch(
        getIdVerificationAsync({
          page: orderListParams.currentPage,
          limit: ORDER_LIMIT,
          status: orderListParams.status,
        }),
      );
    dispatch(getIdVerificationAwaitCountAsync());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderListParams.currentPage,
    orderListParams.category,
    orderListParams.status,
    orderListParams.sortKey,
    orderListParams.isAscending,
    orderListParams.hideSubscriptions,
  ]);

  const onPageChange = (currentPage: number) =>
    setOrderListParams((params) => ({
      ...params,
      sortKey: params.sortKey,
      currentPage,
    }));

  const onHideSubscriptionsChange = (hideSubscriptions: boolean) =>
    setOrderListParams((params) => ({
      ...params,
      currentPage: 1,
      hideSubscriptions,
    }));

  const onSortChange = (sortKeyName: string) =>
    setOrderListParams((params) => ({
      ...params,
      sortKey: sortKeyName,
      currentPage: 1,
      isAscending: getSortDirection({
        currentSortKey: params.sortKey,
        nextSortKey: sortKeyName,
        isAscending: params.isAscending,
        status: params.status,
      }),
    }));

  return (
    <WrappedComponent
      orderList={orderList}
      status={orderStatus}
      category={category}
      loading={orderListLoader}
      sortKey={orderListParams.sortKey}
      isAscending={orderListParams.isAscending}
      hideSubscriptions={orderListParams.hideSubscriptions ?? false}
      onHideSubscriptionsChange={onHideSubscriptionsChange}
      currentPage={orderListParams.currentPage}
      onPageChange={onPageChange}
      onSortChange={onSortChange}
      totalItems={
        category === CLINICAL_REVIEW_ID_CHECK_CATEGORY
          ? totalIdChecks
          : category === CLINICAL_REVIEW_PREAPPROVED_PRODUCT_CATEGORY
          ? totalPreApprovedProductCustomer
          : totalOrders
      }
      pageSize={ORDER_LIMIT}
      idChecks={idChecks}
      preApprovedProductCustomerList={preApprovedProductCustomerList}
    />
  );
};

const getSortDirection = ({
  currentSortKey,
  nextSortKey,
  isAscending,
  status,
}: {
  currentSortKey: string;
  nextSortKey: string;
  isAscending: boolean;
  status: ClinicalReviewStatus;
}) => {
  if (currentSortKey === nextSortKey) {
    return !isAscending;
  }

  if (nextSortKey === "date") {
    return false;
  }

  return usesAscendingSortByDefault(status);
};
